import React, { Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { RoleProvider } from './store/RoleContext'
import { AuthProvider } from './contexts/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'
import SmoothScroll from './components/SmoothScroll'
import PageTransition from './components/PageTransition'
import useScrollToTop from './hooks/useScrollToTop'

// Layouts
import WebsiteLayout from './layouts/WebsiteLayout'
import AdminLayout from './layouts/AdminLayout'
import SuperAdminLayout from './layouts/SuperAdminLayout'
import CMSLayout from './layouts/CMSLayout'

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
)

// Lazy load pages for code splitting
const HomePage = React.lazy(() => import('./pages/website/HomePage'))
const FeaturesPage = React.lazy(() => import('./pages/website/FeaturesPage'))
const PricingPage = React.lazy(() => import('./pages/website/PricingPage'))
const AboutPage = React.lazy(() => import('./pages/website/AboutPage'))
const ContactPage = React.lazy(() => import('./pages/website/ContactPage'))
const PrivacyPolicyPage = React.lazy(() => import('./pages/website/PrivacyPolicyPage'))
const TermsOfServicePage = React.lazy(() => import('./pages/website/TermsOfServicePage'))
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'))
const SignupPage = React.lazy(() => import('./pages/auth/SignupPage'))
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPasswordPage'))
const WelcomePage = React.lazy(() => import('./pages/WelcomePage'))
const SetupAnimationPage = React.lazy(() => import('./pages/auth/SetupAnimationPage'))

// Admin Pages
const IntegratedDashboard = React.lazy(() => import('./pages/admin/IntegratedDashboard'))
const CandidatesPage = React.lazy(() => import('./pages/admin/CandidatesPageEnhanced'))
const DrivesPage = React.lazy(() => import('./pages/admin/DrivesPage'))
const DriveSetupPage = React.lazy(() => import('./pages/admin/DriveSetupPage'))
const DriveSetupWizard = React.lazy(() => import('./pages/admin/DriveSetupWizard'))
const EditDriveWizard = React.lazy(() => import('./pages/admin/EditDriveWizard'))
const CheckInPage = React.lazy(() => import('./pages/admin/CheckInPage'))
const LiveQueuePage = React.lazy(() => import('./pages/admin/LiveQueuePage'))
const FeedbackPage = React.lazy(() => import('./pages/admin/FeedbackPage'))
const SubmitFeedbackPage = React.lazy(() => import('./pages/admin/SubmitFeedbackPage'))
const LobbyDisplayPage = React.lazy(() => import('./pages/admin/LobbyDisplayPage'))
const AnalyticsPage = React.lazy(() => import('./pages/admin/AnalyticsPage'))
const AdminSettingsPage = React.lazy(() => import('./pages/admin/SettingsPage'))
const OrganizationSettingsPage = React.lazy(() => import('./pages/admin/settings/OrganizationSettingsPage'))
const BillingSettingsPage = React.lazy(() => import('./pages/admin/settings/BillingSettingsPage'))
const JobRolesManagementPage = React.lazy(() => import('./pages/admin/settings/JobRolesManagementPage'))
const InterviewerManagementPage = React.lazy(() => import('./pages/admin/settings/InterviewerManagementPage'))
// UserManagementPage moved to tenant-user-management route

const DriveDetailPage = React.lazy(() => import('./pages/admin/DriveDetailPage'))
const DriveDashboard = React.lazy(() => import('./pages/admin/DriveDashboard'))
const InterviewPage = React.lazy(() => import('./pages/admin/InterviewPage'))
const InterviewerLiveQueuePage = React.lazy(() => import('./pages/admin/InterviewerLiveQueuePage'))
const TenantUserManagementPage = React.lazy(() => import('./pages/admin/settings/TenantUserManagementPage'))

// Standalone Pages (no layout)
const ResumeViewer = React.lazy(() => import('./pages/ResumeViewer'))
const PublicCheckinStart = React.lazy(() => import('./pages/public/PublicCheckinStart'))
const PublicCheckinVerify = React.lazy(() => import('./pages/public/PublicCheckinVerify'))
const PublicCheckinReview = React.lazy(() => import('./pages/public/PublicCheckinReview'))
const PublicCheckinToken = React.lazy(() => import('./pages/public/PublicCheckinToken'))

// Super Admin Pages
const SuperAdminDashboard = React.lazy(() => import('./pages/super-admin/SuperAdminDashboard'))
const SuperAdminTenantManagementPage = React.lazy(() => import('./pages/super-admin/TenantManagementPage'))
const GlobalUserManagementPage = React.lazy(() => import('./pages/super-admin/GlobalUserManagementPage'))
const SystemAnalyticsPage = React.lazy(() => import('./pages/super-admin/SystemAnalyticsPage'))
const SuperAdminSettingsPage = React.lazy(() => import('./pages/super-admin/SettingsPage'))

// CMS Pages
const CMSIndexPage = React.lazy(() => import('./pages/super-admin/cms/CMSIndexPage'))
const PricingManagementPage = React.lazy(() => import('./pages/super-admin/cms/PricingManagementPage'))
const FeatureManagementPage = React.lazy(() => import('./pages/super-admin/cms/FeatureManagementPage'))
const TestimonialsManagementPage = React.lazy(() => import('./pages/super-admin/cms/TestimonialsManagementPage'))
const FAQManagementPage = React.lazy(() => import('./pages/super-admin/cms/FAQManagementPage'))
const ContactManagementPage = React.lazy(() => import('./pages/super-admin/cms/ContactManagementPage'))

// Create QueryClient for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

function App() {
  // Use the scroll to top hook for smooth navigation
  useScrollToTop();

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <AuthProvider>
          <RoleProvider>
            <SmoothScroll>
              <PageTransition>
                <div className="min-h-screen bg-white">
                  <Toaster
                    position="top-right"
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: '#363636',
                        color: '#fff',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        zIndex: 9999,
                      },
                      success: {
                        style: {
                          background: '#10b981',
                        },
                      },
                      error: {
                        style: {
                          background: '#ef4444',
                        },
                      },
                    }}
                  />
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
              {/* Public Website Routes */}
              <Route path="/" element={<WebsiteLayout />}>
                <Route index element={<HomePage />} />
                <Route path="features" element={<FeaturesPage />} />
                <Route path="pricing" element={<PricingPage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="privacy" element={<PrivacyPolicyPage />} />
                <Route path="terms" element={<TermsOfServicePage />} />
              </Route>

              {/* Authentication Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/welcome" element={<WelcomePage />} />
              <Route path="/setup" element={<SetupAnimationPage />} />

              {/* Admin Routes */}
              <Route path="/admin" element={
                <ProtectedRoute requiredRoles={['tenant_admin', 'drive_manager', 'interviewer']}>
                  <AdminLayout />
                </ProtectedRoute>
              }>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<IntegratedDashboard />} />
                <Route path="candidates" element={<CandidatesPage />} />
                <Route path="drives" element={<DrivesPage />} />
                <Route path="drive-setup" element={<DriveSetupPage />} />
                <Route path="drive-setup-wizard" element={<DriveSetupWizard />} />
                <Route path="edit-drive-wizard" element={<EditDriveWizard />} />
                <Route path="drive/:id" element={<DriveDetailPage />} />
                <Route path="drive-dashboard/:driveId" element={<DriveDashboard />} />
                <Route path="check-in" element={<CheckInPage />} />
                <Route path="live-queue" element={<LiveQueuePage />} />
                <Route path="interviewer-live-queue" element={<InterviewerLiveQueuePage />} />
                <Route path="interview" element={<InterviewPage />} />
                <Route path="feedback" element={<FeedbackPage />} />
                <Route path="submit-feedback" element={<SubmitFeedbackPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
                                  <Route path="settings/organization" element={<OrganizationSettingsPage />} />
                  <Route path="settings/billing" element={<BillingSettingsPage />} />
                  {/* UserManagementPage moved to tenant-user-management route */}
                  <Route path="settings/role-management" element={<JobRolesManagementPage />} />
                  <Route path="settings/interviewer-management" element={<InterviewerManagementPage />} />
                <Route path="tenant-user-management" element={<TenantUserManagementPage />} />
              </Route>

              {/* Standalone Pages - Full Screen (No Layout) */}
              <Route path="/admin/lobby-display" element={<LobbyDisplayPage />} />
              <Route path="/resume-viewer" element={<ResumeViewer />} />
              <Route path="/public/checkin" element={<PublicCheckinStart />} />
              <Route path="/public/checkin/verify" element={<PublicCheckinVerify />} />
              <Route path="/public/checkin/review" element={<PublicCheckinReview />} />
              <Route path="/public/checkin/token" element={<PublicCheckinToken />} />

              {/* Super Admin Routes */}
              <Route path="/super-admin" element={
                <ProtectedRoute requiredRoles={['super_admin']}>
                  <SuperAdminLayout />
                </ProtectedRoute>
              }>
                <Route index element={<Navigate to="/super-admin/dashboard" replace />} />
                <Route path="dashboard" element={<SuperAdminDashboard />} />
                <Route path="tenants" element={<SuperAdminTenantManagementPage />} />
                <Route path="global-user-management" element={<GlobalUserManagementPage />} />
                <Route path="analytics" element={<SystemAnalyticsPage />} />
                <Route path="settings" element={<SuperAdminSettingsPage />} />
              </Route>

              {/* CMS Routes */}
              <Route path="/super-admin/cms" element={
                <ProtectedRoute requiredRoles={['super_admin']}>
                  <CMSLayout />
                </ProtectedRoute>
              }>
                <Route index element={<CMSIndexPage />} />
                <Route path="pricing" element={<PricingManagementPage />} />
                <Route path="features" element={<FeatureManagementPage />} />
                <Route path="testimonials" element={<TestimonialsManagementPage />} />
                <Route path="faqs" element={<FAQManagementPage />} />
                <Route path="contact" element={<ContactManagementPage />} />
              </Route>

              {/* Fallback route */}
              <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Suspense>
                </div>
              </PageTransition>
            </SmoothScroll>
          </RoleProvider>
        </AuthProvider>
      </HelmetProvider>
    </QueryClientProvider>
  )
}

export default App
